import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const API_BASE = process.env.REACT_APP_API_URL;

export const registerUser = createAsyncThunk(
  "user/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      const incomingEmail = userData.email;
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return rejectWithValue(errorText || "Registration failed");
      }      
      const showSeleniumTools = process.env.REACT_APP_SHOW_SELENIUM_TOOLS === 'true';
      if(showSeleniumTools){
        try{
          const bypass = await fetch(`${API_BASE}/api/sel/testingSelenium`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: incomingEmail }),
          });
        if(bypass.ok){}
        else if(!bypass.ok){}
      }catch(err){}
    }
      return await response.json();
    } catch (err) {
      return rejectWithValue("Network error: " + err.message);
    }
  }
);

// This thunk is updated to avoid the error that was occuring because Json() was actually called twice
export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify(credentials),
});

      const data = await response.json().catch(() => ({}));

      // Check the status using the 'data' we already parsed
      if (!response.ok) {
        // Now you can safely use 'data' because it's already in memory
        const errorMsg = data?.messages?.[0] || "Login Failed";
        return rejectWithValue(errorMsg);
      }

      return data;
    } catch (err) {
      return rejectWithValue("Network error: " + err.message);
    }
  }
);


export const refreshToken = createAsyncThunk(
  "user/refreshToken",
  async (_, {getState, rejectWithValue }) => {
    try {
      const token = getState().user.token;
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      // SAFE parsing (handles empty body)
      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }

      if (!response.ok) {
        return rejectWithValue(
          data?.messages?.[0] || "Session expired. Please log in again."
        );
      }

      return data; // { token }
    } catch (err) {
      return rejectWithValue("Network error: " + err.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "user/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        return rejectWithValue("Logout failed");
      }

      return await response.json();

    } catch (err) {
      return rejectWithValue("Network error: " + err.message);
    }
  }
);


export const fetchUser = createAsyncThunk(
  "user/fetchUser",
  async (_, { getState, rejectWithValue }) => {
    const token = getState().user.token;

    if (!token) return rejectWithValue("No token");

    const response = await fetch(`${API_BASE}/api/users/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return rejectWithValue("Unable to load profile");
    }

    return await response.json();
  }
);


export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (updateData, { rejectWithValue, getState }) => {
    try {
      const token = getState().user.token;
      if (!token) return rejectWithValue("No token found");

      const response = await fetch(`${API_BASE}/api/users/me`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return rejectWithValue(errorText || "Update failed");
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue("Network error: " + err.message);
    }
  }
);
export const updateUserNames = createAsyncThunk(
  "user/updateUserNames",
  async (updateUserData, { rejectWithValue, getState }) => {
    try {
      const token = getState().user.token;
      if (!token) return rejectWithValue("No token found");
      const response = await fetch(`${API_BASE}/api/users/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateUserData),
      });

      if (!response.ok) {
        const errorText = await response.json();
        const errorMsg = errorText.messages && errorText.messages.length > 0 
          ? Array.from(errorText.messages).join(", "):"Update failed";
        return rejectWithValue(errorMsg);
      }
      const data = await response.json();
      return data; 
    } catch (err) {
      return rejectWithValue("Network error: " + err.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/reset-password",
  async ({newPassword, token}, {rejectWithValue, getState}) => {
    try {
      const token = sessionStorage.getItem("resetToken");
      const response = await fetch(`${API_BASE}/api/auth/password/reset-request`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({newPassword})
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMsg = "Unable to reset password";

        if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
          errorMsg = data.messages.join(' ');
        } else if (data.message) {
          errorMsg = data.message;
        }

        return rejectWithValue(errorMsg);
      }

      return data; 
    } catch (err) {
    return rejectWithValue("Network Error: " + err.message);
  }
  }
);


export const verifyEmail = createAsyncThunk(
  "user/verifyEmail",
  async (email, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/password/account-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return rejectWithValue({ 
          message: errorText || "Email verification request failed",
          status: response.status 
        });
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue({ 
        message: "Network error: " + err.message,
        status: 500
      });
    }
  }
);
export const resendEmailVerify = createAsyncThunk(
   'user/resendEmailVerify',
   async (email, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/email/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return rejectWithValue({ 
          message: errorText || "Email verification request failed",
          status: response.status 
        });
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue({ 
        message: "Network error: " + err.message,
        status: 500
      });
    }
  }
)

export const verifyRegistrationEmail = createAsyncThunk(
  'user/verifyRegistrationEmail',
  async (token, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || global.baseUrl;
      const response = await fetch(`${baseUrl}/api/auth/verify?token=${token}`);
      
      // 1. Check if the status is NOT ok
      if (!response.ok) {
        // Try to parse the error message, but handle the case where it's not JSON
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: 'Server returned an error status: ' + response.status };
        }
        return rejectWithValue(errorData.message || 'Verification failed');
      }

      // 2. Only if OK, proceed to parse the actual JSON data
      const data = await response.json();
      return data;
      
    } catch (error) {
      // This catches network issues or JSON parsing failures
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

export const fetchAllUsers = createAsyncThunk(
  "user/fetchAllUsers",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.token;
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return rejectWithValue("Failed to fetch users");
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue("Network error: " + err.message);
    }
  }
)
export const fetchSupportUsers = createAsyncThunk(
  "user/getAllSupport",
  async(_, { getState, rejectWithValue }) => {
      try{
        const token = getState().user.token;
        const response = await fetch(`${API_BASE}/api/admin/users/getAllSupport`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        return rejectWithValue("Failed to fetch users");
      };
      return await response.json();
      }catch(err){
        return rejectWithValue("Network error: " + err.message);
      }
  }
)

export const assignSupportToTicket = createAsyncThunk(
  "user/assignSupportToTicket",
  async ({ ticketId, supportUserId }, { rejectWithValue, getState }) => {
    try {
      const token = getState().user.token;
      if (!token) return rejectWithValue("No token found");
      
      const response = await fetch(`${API_BASE}/api/admin/tickets/${ticketId}/assignee`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          supportUserId
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return {
        ...data, 
        ticketId, 
        supportUserId
      };

    } catch (err) {
      return rejectWithValue("Network error: " + err.message)
    }
  }
)

export const updateUserDetails = createAsyncThunk(
  "user/updateUserDetails",
  async ({ userId, formData }, { rejectWithValue, getState }) => {
    try {
      //get token
      const token = getState().user.token;
      if (!token) return rejectWithValue("No token found");

      //get phone number
      let cleanPhone = null;
      if (formData.phoneNumber && formData.phoneNumber !== "Not Provided" && formData.phoneNumber.trim() !== "") {
        cleanPhone = formData.phoneNumber.replace(/\D/g, '');
      }

      //get role
      let rawRole = "STUDENT";
      if (typeof formData.roles === "string") {
        rawRole = formData.roles;
      } else if (Array.isArray(formData.roles) && formData.roles[0]?.authority) {
        rawRole = formData.roles[0].authority;
      } else if (typeof formData.role === "string") {
        rawRole = formData.role;
      }
      const formattedRole = rawRole.replace("ROLE_", "").toUpperCase();

      const userBackendPayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: cleanPhone,
        roles: [ formattedRole ],
        accountLocked: formData.accountLocked,
        profilePictureURL: formData.profilePictureURL
      };

      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/updateUser`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userBackendPayload),
      });

      if (!response.ok) {
        throw new Error(`Failed to update user:  ${response.status}`);
      }

      const data = await response.json();

      const userFrontendPayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        roles: formattedRole,
        accountLocked: formData.accountLocked,
        profilePictureURL: formData.profilePictureURL,
      };

      return { userId, updateUserDetails: userFrontendPayload, message: data.message };

    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
)

export const toggleAccountLock = createAsyncThunk(
  "user/toggleAccountLock",
  async ({ userId, accountLocked }, { rejectWithValue, getState }) => {
    try {
      const token = getState().user.token;

      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/lock`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ accountLocked }),
      });

      if (!response.ok) {
        const errorText = await response.json();
        const errorMsg = errorText.error
          ? errorText.error
          : `Failed to lock or unlock account: ${response.status}`;
        return rejectWithValue(errorMsg);
      }

      const updatedUser = await response.json();

      return {
        userId,
        accountLocked: updatedUser.accountLocked,
        user: updatedUser
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
)


const userSlice = createSlice({
  name: "user",
  initialState: {
    isAuthenticated: false,
    token: null,
    user: null,
    loading: false,
    authChecked: false,
    emailVerificationLoading: false,
    emailVerificationSuccess: false,
    error: null,
    passwordResetLoading: false,
    usersList: [],
    selectedUser: null,
    isVerified: true,
    status: 'idle',
  },
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { token, user } = action.payload;

        state.token = token;
        state.user = user;
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        if(action.payload === "Please verify your email address before logging in."){
          state.isVerified = false;
          toast.error("Click the link at the bottom to resend email verification link.");
        }
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = null;
        localStorage.setItem("logout-event", Date.now().toString());
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.authChecked = true;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.token = null;
        state.isAuthenticated = false;
        state.user = null;
        state.authChecked = true;
      })    
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserNames.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserNames.fulfilled, (state, action) => {
        state.loading = false;
        state.user = { ...state.user, ...action.payload };
      })
      .addCase(updateUserNames.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyEmail.pending, (state) => {
        state.emailVerificationLoading = true;
        state.emailVerificationSuccess = false;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.emailVerificationLoading = false;
        state.emailVerificationSuccess = true;
        state.isVerified = true;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.emailVerificationLoading = false;
        state.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordLoading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state,action) => {
        state.resetPasswordLoading = false;
        state.error = action.payload;
      })
      .addCase(resendEmailVerify.pending, (state) => {
        state.emailVerificationLoading = true;
        state.emailVerificationSuccess = false;        
        state.error = null;
      })
      .addCase(resendEmailVerify.fulfilled, (state) => {
        state.emailVerificationLoading = false;
        state.emailVerificationSuccess = true; 
        state.error = null;
      })
      .addCase(resendEmailVerify.rejected, (state,action) => {
        state.emailVerificationLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.usersList = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSupportUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupportUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.usersList = action.payload;
      })
      .addCase(fetchSupportUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, updateUserDetails: updatedUser } = action.payload;
        if (!userId || !updatedUser) return;
        let standardizedFields = { ...updatedUser };
        if (updatedUser.roles) {
        if (typeof updatedUser.roles === 'string') {
            standardizedFields.roles = [{ authority: `ROLE_${updatedUser.roles.toUpperCase()}` }];
        } else if (Array.isArray(updatedUser.roles) && typeof updatedUser.roles[0] === 'string') {
            standardizedFields.roles = [{ authority: `ROLE_${updatedUser.roles[0].toUpperCase()}` }];
        }
    }
      const listKey = state.usersList ? 'usersList' : state.usersList ? 'usersList' : state.userList ? 'userList' : null;
      if (listKey) {
        const index = state[listKey].findIndex(user => user.id === userId);
        if (index !== -1) {
            state[listKey][index] = {
                ...state[listKey][index],
                ...standardizedFields
            };
        }
    }

    if (state.selectedUser && state.selectedUser.id === userId) {
        state.selectedUser = {
            ...state.selectedUser,
            ...standardizedFields
        };
    }
      })
      .addCase(updateUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(assignSupportToTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignSupportToTicket.fulfilled, (state, action) => {
        state.loading = false;
        const { ticketId, supportUserId } = action.payload;
        const index = state.usersList.findIndex(user => user.id === supportUserId);

        if (index !== -1) {
          state.usersList[index] = {
            ...state.usersList[index],
            activeTicket: ticketId
          };
        }
      })
      .addCase(assignSupportToTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(toggleAccountLock.fulfilled, (state, action) => {
        const { userId, accountLocked } = action.payload;

        if (userId) {
          const index = state.usersList.findIndex(u => u.id === userId);
          if (index !== -1) {
            state.usersList[index].accountLocked = accountLocked;
          }

          if (state.selectedUser && state.selectedUser.id === userId) {
            state.selectedUser.accountLocked = accountLocked;
          }
        }
      })
      .addCase(toggleAccountLock.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setSelectedUser,logout, clearError} = userSlice.actions;
export default userSlice.reducer;

